import { config } from '@shopee-user/platform';
import { Locale } from '@shopee-user/global';

export type SupportedLocale =
  | Locale.ID
  | Locale.MY
  | Locale.TH
  | Locale.VN
  | Locale.PH;

const AUTHENTICATE_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  ID:
    'https://help.shopee.co.id/portal/article/72332-Mitra-Pengemudi-ShopeeFood?source=2',
  MY: 'https://help.shopee.com.my/portal/2/article/174650', // https://jira.shopee.io/browse/SPB-35114
  TH: 'https://help.shopee.co.th/portal?source=2',
  // https://jira.shopee.io/browse/SPB-20074
  VN: 'https://help.cs.shopeefood.vn/portal/104',
  PH: '',
};

// Copied over from authenticate help link map, to change if different from authenticate link.
const SIGNUP_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'https://help.shopee.co.id/portal?source=2',
  MY:
    'https://help.shopee.com.my/portal/2/article/116866?previousPage=other%20articles', // https://jira.shopee.io/browse/SPB-35114
  TH: 'https://help.shopee.co.th/portal?source=2',
  // https://confluence.shopee.io/display/SAUT/20240131+-+%5BVN%5D+Enable+Self+Sign+Up+Registration
  // Note: The link need to be encoded to open in iOS
  VN: 'https://help.cs.shopeefood.vn/portal/104/article/71255',
  PH: '',
};

const AUTHENTICATE_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'https://help.shopee.co.id/portal?source=2',
  MY:
    'https://help.shopee.com.my/s/article/ShopeeFood-Why-I-unable-to-log-into-the-ShopeeFood-Delivery-Partner-app',
  TH: 'https://help.shopee.co.th/portal?source=2',
  // Note: The link need to be encoded to open in iOS
  VN:
    'https://help.cs.shopeefood.vn/portal/104/category/626-T%C3%A0i-kho%E1%BA%A3n-%E1%BB%A8ng-d%E1%BB%A5ng/627-T%C3%A0i-kho%E1%BA%A3n?page=1',
  PH: '',
};
//
// Copied over from authenticate help link map, to change if different from authenticate link.
const ACCOUNT_SUSPENDED_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  ...AUTHENTICATE_CONTACT_US_LINK_MAP,
  VN:
    'https://help.cs.shopeefood.vn/portal/article/71466-L%C3%A0m-th%E1%BA%BF-n%C3%A0o-khi-b%E1%BB%8B-kh%C3%B3a-thi%E1%BA%BFt-b%E1%BB%8B%2Ft%C3%A0i-kho%E1%BA%A3n-ng%C6%B0ng-nh%E1%BA%ADn-%C4%91%C6%A1n?previousPage=search%20recommendation%20bar&source=104',
};

const USER_ACCOUNT_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  ID:
    'https://help.shopee.co.id/portal/article/72329-Bagaimana-cara-melakukan-perubahan-data-di-aplikasi-Mitra-Pengemudi-ShopeeFood?source=2',
  MY:
    'https://help.shopee.com.my/s/article/ShopeeFood-How-to-change-my-account-information-in-ShopeeFood-Delivery-Partner-app',
  TH: 'https://help.shopee.co.th/portal?source=2',
  VN: 'https://help.cs.shopeefood.vn/portal/104',
  PH: '',
};

const LOGIN_FRAUD_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'https://help.shopee.co.id/s/article/General-Driver-ShopeeFood',
  MY:
    'https://help.shopee.com.my/my/s/article/ShopeeFood-Rakan-Penghantar-Saya-menerima-pesanan-Error-Login-Failed-K11-Apa-yang-perlu-saya-lakukan',
  TH: 'https://help.shopee.co.th/portal?source=2',
  VN: 'https://help.cs.shopeefood.vn/portal/104',
  PH: '',
};

const CHANGE_PHONE_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'https://help.shopee.co.id/portal/article/72212',
  MY: '',
  TH:
    'https://help.shopee.co.th/portal/webform/e6975ede7b934b1186aa4f2dcba52083',
  VN: '',
  PH: '',
};

const TERMS_OF_SERVICE_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'https://help.shopee.co.id/portal/article/71206',
  MY: 'https://shopee.com.my/docs/7033',
  TH:
    'https://help.shopee.co.th/portal/article/77259?previousPage=other%20articles',
  // https://confluence.shopee.io/display/SAUT/20240131+-+%5BVN%5D+Enable+Self+Sign+Up+Registration
  // Note: The link need to be encoded to open in iOS
  VN:
    'https://driver.shopeefood.vn/trung-tam-ho-tro/dieu-khoan-su-dung-chinh-sach-bao-mat/',
  PH: 'https://help.shopee.ph/portal/2/article/188467',
};

const PRIVACY_POLICY_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'https://help.shopee.co.id/portal/article/73835',
  MY: '',
  TH:
    'https://help.shopee.co.th/portal/article/77248?previousPage=other%20articles',
  // https://confluence.shopee.io/display/SAUT/20240131+-+%5BVN%5D+Enable+Self+Sign+Up+Registration
  // Note: The link need to be encoded to open in iOS
  VN:
    'https://driver.shopeefood.vn/trung-tam-ho-tro/dieu-khoan-su-dung-chinh-sach-bao-mat/#chinh-sach-bao-mat',
  PH: 'https://help.shopee.ph/portal/2/article/188461',
};

const PHONE_OR_EMAIL_LINKED_CONTACT_US_LINK_MAP: Record<
  SupportedLocale,
  string
> = {
  ID: '',
  MY: '',
  TH: '',
  VN: '',
  PH: '',
};

const TOC_ACCOUNT_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  ID: '',
  MY: '',
  TH: '',
  VN: '',
  PH: '',
};

// Deeplinks to native IOS account deletion page, open with webbridge openExternalLink
// https://confluence.shopee.io/display/SPAT/openExternalLink
// Use country specific schemes to support one device having apps from multiple regions
const ACCOUNT_DELETE_IOS_LINK_MAP: Record<SupportedLocale, string> = {
  ID: '',
  MY: 'foodymy://driver/n/driver/account_deletion',
  TH: 'foodyth://driver/n/driver/account_deletion',
  VN: 'foodyvn://driver/n/driver/account_deletion',
  PH: 'foodyph://driver/n/driver/account_deletion',
};

// Deeplinks to native Android account deletion page, open with webbridge openExternalLink
// https://confluence.shopee.io/display/SPAT/openExternalLink
// Use country specific schemes to support one device having apps from multiple regions
const ACCOUNT_DELETE_ANDROID_LINK_MAP: Record<SupportedLocale, string> = {
  ID: 'foodyid://driver/n/driver/account_deletion',
  MY: 'foodymy://driver/n/driver/account_deletion',
  TH: 'foodyth://driver/n/driver/account_deletion',
  VN: 'foodyvn://driver/n/driver/account_deletion',
  PH: 'foodyph://driver/n/driver/account_deletion',
};

// Use country specific schemes to support one device having apps from multiple regions
const CHANGE_PHONE_NATIVE_VERIFICATION_LINK_MAP: Record<
  SupportedLocale,
  string
> = {
  ID: 'foodyid://driver/n/driver/slc/change_phone_slc',
  TH: 'foodyth://driver/n/driver/slc/change_phone_slc',
  MY: '',
  VN: '',
  PH: 'foodyph://driver/n/driver/slc/change_phone_slc',
};

export const AUTHENTICATE_HELP_LINK =
  AUTHENTICATE_HELP_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const ONE_CREDENTIAL_RECLAIM_PHONE_FAILED_HELP_LINK = AUTHENTICATE_HELP_LINK;
export const SIGNUP_HELP_LINK =
  SIGNUP_HELP_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const ACCOUNT_SUSPENDED_HELP_LINK =
  ACCOUNT_SUSPENDED_HELP_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const RESET_PASSWORD_INFO_LINK = '';
export const AUTHENTICATE_CONTACT_US_LINK =
  AUTHENTICATE_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const USER_ACCOUNT_CONTACT_US_LINK =
  USER_ACCOUNT_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const LOGIN_FRAUD_CONTACT_US_LINK =
  LOGIN_FRAUD_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const CHANGE_PHONE_HELP_LINK =
  CHANGE_PHONE_HELP_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const ACCOUNT_DELETE_IOS_LINK =
  ACCOUNT_DELETE_IOS_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const ACCOUNT_DELETE_ANDROID_LINK =
  ACCOUNT_DELETE_ANDROID_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const CHANGE_PHONE_NATIVE_VERIFICATION_LINK =
  CHANGE_PHONE_NATIVE_VERIFICATION_LINK_MAP[config.LOCALE as SupportedLocale] ||
  '';
export const TERMS_OF_SERVICE_LINK =
  TERMS_OF_SERVICE_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const PRIVACY_POLICY_LINK =
  PRIVACY_POLICY_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const COOKIE_POLICY_LINK = '';
export const PHONE_OR_EMAIL_LINKED_CONTACT_US_LINK =
  PHONE_OR_EMAIL_LINKED_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale] ||
  '';
export const TOC_ACCOUNT_CONTACT_US_LINK =
  TOC_ACCOUNT_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale];
export const TERMS_OF_SERVICE_WEBVIEW_LINK = TERMS_OF_SERVICE_LINK;
export const PRIVACY_POLICY_WEBVIEW_LINK = PRIVACY_POLICY_LINK;
export const DATA_PROTECTION_POLICY_LINK = '';
export const PLATFORM_PARTNER_RULES_LINK = '';
